import _ from 'lodash';
import type { GridApi, IRowNode } from 'ag-grid-community';
import {
  ColumnOrRowWithDefinitions,
  TreemapBreadcrumbs,
  TreemapDisplayScalar,
  TreemapParent,
  TreemapViewSettings,
} from '@/tableBuilder/tableBuilder.types';
import type { TreemapItem } from '@/treemap/TreemapChart.atom';
import { priorityColors } from '@/services/systemConfiguration.utilities';
import { TREND_SIGNAL_STATS } from '@/trendData/trendData.constants';
import type { ConditionItem, ScalarItem, SeriesItem } from '@/trendData/trendData.types';
import { ASSET_PATH_COLUMN_PREFIX, ASSET_PATH_ID_COLUMN_PREFIX, ROW_ID } from '@/tableBuilder/tableBuilder.constants';

const DEFAULT_NEUTRAL_COLOR = '#068C45';
const COUNT_STATISTIC_KEY = 'statistics.count';

/**
 * Updates the treemap settings and calls the provided setter.
 * @param {Object} params - The parameters object.
 * @param {GridApi} params.agGridApi - The grid API instance.
 * @param {ColumnOrRowWithDefinitions[]} params.simpleColumns - The columns to display.
 * @param {TreemapViewSettings} params.currentSettings - The current treemap settings.
 * @param {(newSettings: TreemapViewSettings) => void} params.setTreemapViewSettings - The setter function.
 * @param {ConditionItem[]} params.conditions - The list of conditions.
 * @returns {void} Updates the treemapViewSettings & returns void
 */
type GetTreemapStructure = {
  simpleColumns: ColumnOrRowWithDefinitions[];
  currentSettings: TreemapViewSettings;
  setTreemapViewSettings: (newSettings: TreemapViewSettings) => void;
  conditions: ConditionItem[];
  agGridApi?: GridApi | null;
  signals: (ScalarItem | SeriesItem)[];
};

type BuildTree = Pick<GetTreemapStructure, 'simpleColumns'> & {
  breadcrumbs: TreemapBreadcrumbs;
  conditions: ConditionItem[];
  node: IRowNode;
  parent: TreemapParent;
  signals: (ScalarItem | SeriesItem)[];
};

/**
 * Returns the neutral color for treemap assets.
 * @returns {string} The neutral color hex code.
 */
const getNeutralColor = (): string => {
  const neutralColor = _.chain(priorityColors())
    .find((priority: any) => priority.level === 0)
    .get('color', DEFAULT_NEUTRAL_COLOR)
    .value();

  const color = _.toLower(neutralColor);
  return color === '#ffffff' || color === 'unset' ? DEFAULT_NEUTRAL_COLOR : neutralColor;
};

/**
 * Finds and returns the root node from the grid API.
 * @param {Object} params - The parameters object.
 * @param {GridApi} params.agGridApi - The grid API instance.
 * @returns {IRowNode | null} The root node or null if not found.
 */
const getRootNode = ({ agGridApi }: Pick<GetTreemapStructure, 'agGridApi'>): IRowNode | null => {
  let localRootNode: IRowNode | null = null;
  agGridApi?.forEachNode((node) => {
    if (node?.group && node?.level === 0) {
      localRootNode = node;
    }
  });

  return localRootNode;
};

/**
 * Determines the color for a treemap asset based on conditions and capsule count.
 * This function recursively checks the children of the node to find the leaf group
 * within that leaf group, we verify the capsule count against the conditions and return the appropriate color.
 * If the highest priority condition is met, it returns that color, if not it moves to the next condition.
 * If no conditions are met, it returns a neutral color.
 * @param {Object} params - The parameters object.
 * @param {IRowNode} params.child - The node to check.
 * @param {ConditionItem[]} params.conditions - The list of conditions.
 * @returns {string} The color hex code for the asset.
 */
const getAssetColor = ({ child, conditions }: { child: IRowNode; conditions: ConditionItem[] }): string => {
  const neutralColor = getNeutralColor();
  const topPriorityColor = _.first(conditions)?.color || neutralColor;

  // Base case: no children or no conditions
  if (!_.isArray(child?.childrenAfterGroup) || _.isEmpty(conditions)) {
    return neutralColor;
  }

  // If not a leaf group, recursively evaluate children
  if (!child.leafGroup) {
    let resolvedColor = neutralColor;

    _.forEach(child.childrenAfterGroup, (nestedChild) => {
      const nestedColor = getAssetColor({ child: nestedChild, conditions });

      if (nestedColor === topPriorityColor) {
        resolvedColor = topPriorityColor;
        return false; // Exit early if the top priority color is found
      }

      if (nestedColor !== neutralColor) {
        resolvedColor = nestedColor;
      }
    });

    return resolvedColor;
  }

  // At leaf level: match the first condition (in order) with any grandchild
  const matchedCondition = _.find(conditions, (condition) =>
    _.some(child.childrenAfterGroup, (grandChild) => {
      const rowId = grandChild?.data?.[ROW_ID];
      const count = grandChild?.data?.[COUNT_STATISTIC_KEY]?.rawValue;
      return rowId && rowId.includes(condition.id) && count > 0;
    }),
  );

  return matchedCondition?.color || neutralColor;
};

/**
 * Builds the assetPathId that we'll use to identify the asset that we need to swap to
 * @param {string} assetId - The key of the asset.
 * @returns {string} The formatted asset key.
 */
export const buildAssetKey = (assetId: string): string => {
  const regex = new RegExp(`${ASSET_PATH_COLUMN_PREFIX}[^-\\s]+`, 'g');
  const matches = assetId.match(regex);
  return matches ? matches[matches.length - 1].replace(ASSET_PATH_COLUMN_PREFIX, ASSET_PATH_ID_COLUMN_PREFIX) : '';
};

/**
 * Builds the breadcrumbs for the treemap view based on the current node and settings.
 * @param {Object} params - The parameters object.
 * @param {IRowNode} params.node - The current node.
 * @param {TreemapViewSettings} params.currentSettings - The current treemap settings.
 * @returns {TreemapBreadcrumbs} The breadcrumbs array.
 */
const buildBreadcrumbs = ({
  node,
  currentSettings,
}: {
  node: IRowNode;
  currentSettings: TreemapViewSettings;
}): TreemapBreadcrumbs => {
  const breadcrumbs: TreemapBreadcrumbs = [];
  let currentNode: IRowNode | null = node;

  while (currentNode && !currentNode.leafGroup) {
    if (currentNode?.id && currentNode?.key) {
      breadcrumbs.push({ id: currentNode.id, name: currentNode.key, level: currentNode.level });
    }

    currentNode =
      currentNode?.childrenAfterGroup?.find((child) => child?.id && child.id === currentSettings.parent.id) ||
      currentNode?.childrenAfterGroup?.[0] ||
      null;
  }

  if (!breadcrumbs.some((crumb) => crumb?.id === currentSettings?.parent?.id)) {
    return breadcrumbs;
  } else {
    const parentLevel = breadcrumbs.find((crumb) => crumb.id === currentSettings.parent.id)?.level ?? 0;
    return breadcrumbs.filter(
      (crumb) => (crumb?.level !== undefined && crumb.level < parentLevel) || crumb.id === currentSettings.parent.id,
    );
  }
};

/**
 * Builds the leaf asset items for the treemap.
 * @param {Object} params - The parameters object.
 * @param {ColumnOrRowWithDefinitions[]} params.simpleColumns - The columns to display.
 * @param {IRowNode} params.node - The node to process.
 * @param {ConditionItem[]} params.conditions - The list of conditions.
 * @param {(SeriesItem | ScalarItem)[]} params.signals - The list of series/scalar type items
 * @returns {TreemapItem[]} The array of leaf asset items.
 */
const buildLeafAssets = ({
  conditions,
  node,
  signals,
  simpleColumns,
}: Omit<BuildTree, 'parent' | 'breadcrumbs'>): TreemapItem[] => {
  const statsKeys = simpleColumns.filter((col) => col.key.startsWith('statistics.'));

  if (!node?.childrenAfterGroup?.length) {
    return [];
  }

  return node?.childrenAfterGroup
    ?.map((child) => {
      const assetPathId = buildAssetKey(child.id ?? '');
      const data = child?.childrenAfterGroup?.[0]?.data ?? {};
      const matchingKey = Object.keys(data).find((k) => k.startsWith(assetPathId));

      return {
        size: 1,
        isLeafAsset: true,
        displayScalars: buildDisplayScalars(child, statsKeys, signals),
        asset: {
          id: matchingKey && data[matchingKey]?.rawValue,
          name: child.key ?? '',
        },
        color: getAssetColor({ child, conditions }),
      };
    })
    .filter((item) => item.asset.id);
};

/**
 * Builds the display scalars for a node.
 * @param {IRowNode} node - The node to process.
 * @param {ColumnOrRowWithDefinitions[]} statsKeys - The statistics columns.
 * @param {(SeriesItem | ScalarItem)[]} signals - The list of series/scalar type items
 * @returns {TreemapDisplayScalar[]} The array of display scalar objects.
 */
const buildDisplayScalars = (
  node: IRowNode,
  statsKeys: ColumnOrRowWithDefinitions[],
  signals: (SeriesItem | ScalarItem)[],
): Array<TreemapDisplayScalar> => {
  const displayScalars: Array<TreemapDisplayScalar> = [];
  const trendStats = TREND_SIGNAL_STATS.map((stat) => stat.key);

  signals?.forEach((signal) => {
    statsKeys
      .filter(
        (col) =>
          col.key !== COUNT_STATISTIC_KEY &&
          trendStats.includes(col.key) &&
          (('valueUnitOfMeasure' in signal && signal.valueUnitOfMeasure !== 'string') || col.isStringCompatible),
      )
      .forEach((statKey) => {
        node?.childrenAfterGroup?.forEach((child) => {
          if (child.data[statKey.key] && child.data[ROW_ID].includes(signal.id)) {
            const formatOptions =
              signal.formatOptions && typeof signal.formatOptions.format === 'string'
                ? {
                    format: signal.formatOptions.format,
                    ...(typeof signal.formatOptions.stringFormat === 'string'
                      ? { stringFormat: signal.formatOptions.stringFormat }
                      : {}),
                  }
                : undefined;

            displayScalars.push({
              uom: child.data[statKey.key].units,
              value: child.data[statKey.key].rawValue,
              isUncertain: child.data[statKey.key].isUncertain ?? false,
              title: statKey.title ?? '',
              signal: signal.name,
              ...(formatOptions ? { formatOptions } : {}),
            });
          }
        });
      });
  });

  return displayScalars;
};

/**
 * Builds the middle asset items for the treemap.
 * @param {Object} params - The parameters object.
 * @param {IRowNode} params.node - The node to process.
 * @param {ConditionItem[]} params.conditions - The list of conditions.
 * @returns {TreemapItem[]} The array of middle asset items.
 */
const buildMiddleAssets = ({ node, conditions }: { node: IRowNode; conditions: ConditionItem[] }): TreemapItem[] => {
  if (!node?.childrenAfterGroup?.length) {
    return [];
  }

  return node.childrenAfterGroup.map((child) => {
    return {
      size: 1,
      isLeafAsset: false,
      asset: {
        id: child.id ?? '',
        name: child.key ?? '',
      },
      displayScalars: [],
      color: getAssetColor({ child, conditions }),
    };
  });
};

/**
 * Recursively builds the treemap tree structure.
 * @param {Object} params - The parameters object.
 * @param {IRowNode} params.node - The current node.
 * @param {TreemapBreadcrumbs} params.breadcrumbs - The breadcrumbs array.
 * @param {ColumnOrRowWithDefinitions[]} params.simpleColumns - The columns to display.
 * @param {TreemapParent} params.parent - The parent node.
 * @param {ConditionItem[]} params.conditions - The list of conditions.
 * @param {(SeriesItem | ScalarItem)[]} params.signals - The list of series/scalar type items
 * @returns {TreemapItem[]} The tree structure as an array of items.
 */
const buildTree = ({ node, breadcrumbs, simpleColumns, parent, conditions, signals }: BuildTree): TreemapItem[] => {
  if (node?.key === parent?.name) {
    const hasLeafGroupChildren = node?.childrenAfterGroup?.some((child) => child.leafGroup);

    return hasLeafGroupChildren
      ? buildLeafAssets({ simpleColumns, node, conditions, signals })
      : buildMiddleAssets({ node, conditions });
  }

  if (!node || !node?.childrenAfterGroup || !node?.childrenAfterGroup.length) {
    return [];
  }

  const children = (node?.childrenAfterGroup || []).filter((child) => child.group);
  const nextChild = children.find((child) => child?.id && breadcrumbs.some((crumb) => crumb.id === child.id));

  return buildTree({ node: nextChild ?? children[0], simpleColumns, breadcrumbs, parent, conditions, signals });
};

/**
 * Builds the treemap structure and calls the provided setter on initial render if needed.
 * @param {Object} params - The parameters object.
 * @param {GridApi} params.agGridApi - The grid API instance.
 * @param {ColumnOrRowWithDefinitions[]} params.simpleColumns - The columns to display.
 * @param {TreemapViewSettings} params.currentSettings - The current treemap settings.
 * @param {(newSettings: TreemapViewSettings) => void} params.setTreemapViewSettings - The setter function.
 * @param {ConditionItem[]} params.conditions - The list of conditions.
 * @param {(SeriesItem | ScalarItem)[]} params.signals - The list of series/scalar type items
 * @returns {(TreemapViewSettings & { tree: TreemapItem[] }) | undefined} Updates the treemapViewSettings & returns void
 */
export const getTreemapStructure = ({
  agGridApi,
  simpleColumns,
  currentSettings,
  conditions,
  setTreemapViewSettings,
  signals,
}: GetTreemapStructure): TreemapViewSettings & { tree: TreemapItem[] } => {
  const rootNode = getRootNode({ agGridApi });
  if (!rootNode) {
    return { ...currentSettings, tree: [] };
  }

  const breadcrumbs = buildBreadcrumbs({ node: rootNode, currentSettings });
  const parent = _.last(breadcrumbs) ?? currentSettings.parent;
  const tree = buildTree({ node: rootNode, simpleColumns, breadcrumbs, parent, conditions, signals });

  if (currentSettings.breadcrumbs.length === 0 || !currentSettings.parent.id) {
    setTreemapViewSettings({
      ...currentSettings,
      breadcrumbs,
      parent,
    });
  }

  return {
    ...currentSettings,
    breadcrumbs,
    parent,
    tree,
  };
};
